import React, { useCallback, useMemo, useState } from "react";
import Cropper from "react-easy-crop";
import cropImage from "./cropImage";

function ImageCropModal({
  isOpen,
  imageSrc,
  fileName,
  fileType,
  aspect = 16 / 9,
  onClose,
  onCropped,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const canCrop = Boolean(isOpen && imageSrc);

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const title = useMemo(() => "Crop Image", []);

  const handleSave = async () => {
    if (!canCrop || !croppedAreaPixels) return;
    setIsSaving(true);
    try {
      const { file, dataUrl } = await cropImage({
        imageSrc,
        pixelCrop: croppedAreaPixels,
        fileName,
        fileType,
      });
      if (typeof onCropped === "function") {
        await onCropped({ file, previewUrl: dataUrl });
      }
      if (typeof onClose === "function") onClose();
    } catch (e) {
      console.error("Crop failed", e);
      window.alert("Unable to crop image. Please try a different image.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={isSaving ? undefined : onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={isSaving ? undefined : onClose}
              disabled={isSaving}
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body">
            <div
              style={{
                position: "relative",
                width: "100%",
                height: 420,
                background: "#111",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              {imageSrc ? (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  objectFit="contain"
                />
              ) : null}
            </div>

            <div className="mt-3">
              <label className="form-label">Zoom</label>
              <input
                type="range"
                className="form-range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={isSaving ? undefined : onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving || !croppedAreaPixels}
              style={isSaving ? { opacity: 0.75, cursor: "not-allowed" } : {}}
            >
              {isSaving ? "Cropping..." : "Crop & Use"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageCropModal;
